import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AcceptClientInvitePage from "@/pages/AcceptClientInvitePage";
import AcceptProviderInvitePage from "@/pages/AcceptProviderInvitePage";
import PolicyGatePage from "@/pages/PolicyGatePage";
import SecurityPage from "@/pages/SecurityPage";
import TwoFactorRequiredPage from "@/pages/TwoFactorRequiredPage";
import AppAssetsPage from "@/pages/AssetsPage";
import AssignmentsPage from "@/pages/AssignmentsPage";
import CandidatesPage from "@/pages/CandidatesPage";
import ClientAccountsPage from "@/pages/ClientAccountsPage";
import ClientAssignmentsPage from "@/pages/ClientAssignmentsPage";
import CompliancePage from "@/pages/CompliancePage";
import EvaluationBillingPage from "@/pages/EvaluationBillingPage";
import ClientDocumentsPage from "@/pages/ClientDocumentsPage";
import ContractLibraryPage from "@/pages/ContractLibraryPage";
import ClientReportsPage from "@/pages/ClientReportsPage";
import ProviderManagementPage from "@/pages/ProviderManagementPage";
import ProviderPortalPage from "@/pages/ProviderPortalPage";
import ProviderHomePage from "@/pages/provider/ProviderHomePage";
import ProviderMyWorkPage from "@/pages/provider/ProviderMyWorkPage";
import ProviderWorkItemPage from "@/pages/provider/ProviderWorkItemPage";
import ProviderAssignmentPage from "@/pages/provider/ProviderAssignmentPage";
import ProviderTimeAndPayPage from "@/pages/provider/ProviderTimeAndPayPage";
import ProviderFilePage from "@/pages/provider/ProviderFilePage";
import ProviderCredentialsPage from "@/pages/provider/ProviderCredentialsPage";
import EvaluationPipelinePage from "@/pages/EvaluationPipelinePage";
import ProviderRegistrationPage from "@/pages/ProviderRegistrationPage";
import PricingToolsPage from "@/pages/PricingToolsPage";
import ProjectionsPage from "@/pages/ProjectionsPage";
import ClientEvaluationRequestPage from "@/pages/ClientEvaluationRequestPage";
import ChangeReviewPage from "@/pages/ChangeReviewPage";
import CredentialIntelligencePage from "@/pages/CredentialIntelligencePage";
import SupervisionVerificationPage from "@/pages/SupervisionVerificationPage";
import TasksPage from "@/pages/TasksPage";
import ClientReviewPage from "@/pages/ClientReviewPage";
import ClientsPage from "@/pages/ClientsPage";
import ConnecteamPage from "@/pages/ConnecteamPage";
import ClosuresPage from "@/pages/ClosuresPage";
import NotificationLogPage from "@/pages/NotificationLogPage";
import DashboardPage from "@/pages/DashboardPage";
import LoginPage from "@/pages/LoginPage";
import MasterStaffingBoardPage from "@/pages/MasterStaffingBoardPage";
import ReportsPage from "@/pages/ReportsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import OperationsCenterPage from "@/pages/OperationsCenterPage";
import ClientPortalPreviewPage from "@/pages/ClientPortalPreviewPage";
import ClientPortalApp, { ClientPortalPreviewApp } from "@/portal/ClientPortalApp";
import StaffingRequestsPage from "@/pages/StaffingRequestsPage";
import TeamDirectoryPage from "@/pages/TeamDirectoryPage";
import { routeRoles } from "@/lib/accessPolicy";
import { mobileLanding } from "@/lib/pwa";
import { InstallPrompt } from "@/components/InstallPrompt";

function HomeRedirect({ user }) {
  const landing = mobileLanding(user);
  if (landing) return <Navigate to={landing} replace />;
  return <DashboardPage />;
}

function RequireRoles({ allowedRoles, children, user }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  const { user, isChecking } = useAuth();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="app-loading" data-testid="app-loading-state">
        Loading your secure workspace…
      </div>
    );
  }

  if (user && user.password_change_required && location.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }
  if (user && !user.password_change_required && user.policy_acceptance_required) {
    return <PolicyGatePage />;
  }
  if (user && !user.password_change_required && user.two_factor_setup_required) {
    return <TwoFactorRequiredPage />;
  }

  return (
    <Routes>
      <Route
        path="/account/security"
        element={user ? <SecurityPage /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/set-password"
        element={
          !user ? (
            <Navigate to="/login" replace />
          ) : user.password_change_required ? (
            <SetPasswordPage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/client-invite/accept" element={<AcceptClientInvitePage />} />
      <Route path="/provider-invite/accept" element={<AcceptProviderInvitePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route
        path="/register"
        element={user ? <Navigate to="/" replace /> : <ProviderRegistrationPage />}
      />
      <Route
        path="/"
        element={
          user ? (
            <HomeRedirect user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="/portal/*" element={user ? (user.role === "client" ? <ClientPortalApp /> : <Navigate to="/" replace />) : <Navigate to="/login" replace />} />
      <Route
        path="/client-portal-preview"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-portal-preview"]} user={user}>
            <ClientPortalPreviewPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-portal-preview/:clientId/*"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-portal-preview"]} user={user}>
            <ClientPortalPreviewApp />
          </RequireRoles>
        }
      />
      <Route
        path="/assets"
        element={
          <RequireRoles allowedRoles={routeRoles["/assets"]} user={user}>
            <AppAssetsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/my-assets"
        element={
          <RequireRoles allowedRoles={routeRoles["/my-assets"]} user={user}>
            <AppAssetsPage portalMode />
          </RequireRoles>
        }
      />
      <Route
        path="/contracts"
        element={
          <RequireRoles allowedRoles={routeRoles["/contracts"]} user={user}>
            <ContractLibraryPage />
          </RequireRoles>
        }
      />
      <Route
        path="/pricing-tools"
        element={
          <RequireRoles allowedRoles={routeRoles["/pricing-tools"]} user={user}>
            <PricingToolsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/pay-rate-calculator"
        element={
          <RequireRoles allowedRoles={routeRoles["/pay-rate-calculator"]} user={user}>
            <PricingToolsPage initialTab="pay-rate" />
          </RequireRoles>
        }
      />
      <Route
        path="/projections"
        element={
          <RequireRoles allowedRoles={routeRoles["/projections"]} user={user}>
            <ProjectionsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/providers"
        element={
          <RequireRoles allowedRoles={routeRoles["/providers"]} user={user}>
            <ProviderManagementPage />
          </RequireRoles>
        }
      />
      <Route
        path="/provider-portal"
        element={
          <RequireRoles allowedRoles={routeRoles["/provider-portal"]} user={user}>
            {user?.role === "provider" ? <ProviderHomePage /> : <ProviderPortalPage />}
          </RequireRoles>
        }
      />
      <Route path="/provider-portal/work" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/work"]} user={user}>{user?.role === "provider" ? <ProviderMyWorkPage /> : <ProviderPortalPage />}</RequireRoles>} />
      <Route path="/provider-portal/work/:itemId" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/work/:itemId"]} user={user}><ProviderWorkItemPage /></RequireRoles>} />
      <Route path="/provider-portal/assignments/:assignmentId" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/work/:itemId"]} user={user}><ProviderAssignmentPage /></RequireRoles>} />
      <Route path="/provider-portal/pay" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/pay"]} user={user}><ProviderTimeAndPayPage /></RequireRoles>} />
      <Route path="/provider-portal/file" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/file"]} user={user}><ProviderFilePage /></RequireRoles>} />
      <Route path="/provider-portal/file/:section" element={<RequireRoles allowedRoles={routeRoles["/provider-portal/file"]} user={user}><ProviderFilePage /></RequireRoles>} />
      <Route path="/my-credentials" element={<RequireRoles allowedRoles={routeRoles["/my-credentials"]} user={user}><ProviderCredentialsPage /></RequireRoles>} />
      <Route path="/evaluation-pipeline" element={<RequireRoles allowedRoles={routeRoles["/evaluation-pipeline"]} user={user}><EvaluationPipelinePage /></RequireRoles>} />
      <Route
        path="/client-evaluation-request"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-evaluation-request"]} user={user}>
            <ClientEvaluationRequestPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-assignments"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-assignments"]} user={user}>
            <ClientAssignmentsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-documents"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-documents"]} user={user}>
            <ClientDocumentsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-reports"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-reports"]} user={user}>
            <ClientReportsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/evaluation-review"
        element={<Navigate replace to="/evaluation-pipeline" />}
      />
      <Route
        path="/change-review"
        element={
          <RequireRoles allowedRoles={routeRoles["/change-review"]} user={user}>
            <ChangeReviewPage />
          </RequireRoles>
        }
      />
      <Route
        path="/operations-center"
        element={
          <RequireRoles allowedRoles={routeRoles["/operations-center"]} user={user}>
            <OperationsCenterPage />
          </RequireRoles>
        }
      />
      <Route
        path="/credential-intelligence"
        element={
          <RequireRoles allowedRoles={routeRoles["/credential-intelligence"]} user={user}>
            <CredentialIntelligencePage />
          </RequireRoles>
        }
      />
      <Route
        path="/evaluation-billing"
        element={
          <RequireRoles allowedRoles={routeRoles["/evaluation-billing"]} user={user}>
            <EvaluationBillingPage />
          </RequireRoles>
        }
      />
      <Route
        path="/compliance"
        element={
          <RequireRoles allowedRoles={routeRoles["/compliance"]} user={user}>
            <CompliancePage />
          </RequireRoles>
        }
      />
      <Route
        path="/supervision-verification"
        element={
          <RequireRoles allowedRoles={routeRoles["/supervision-verification"]} user={user}>
            <SupervisionVerificationPage />
          </RequireRoles>
        }
      />
      <Route
        path="/connecteam"
        element={
          <RequireRoles allowedRoles={routeRoles["/connecteam"]} user={user}>
            <ConnecteamPage />
          </RequireRoles>
        }
      />
      <Route
        path="/closures"
        element={
          <RequireRoles allowedRoles={routeRoles["/closures"]} user={user}>
            <ClosuresPage />
          </RequireRoles>
        }
      />
      <Route
        path="/notification-log"
        element={
          <RequireRoles allowedRoles={routeRoles["/notification-log"]} user={user}>
            <NotificationLogPage />
          </RequireRoles>
        }
      />
      <Route
        path="/tasks"
        element={
          <RequireRoles allowedRoles={routeRoles["/tasks"]} user={user}>
            <TasksPage />
          </RequireRoles>
        }
      />
      <Route
        path="/margin-analyzer"
        element={
          <RequireRoles allowedRoles={routeRoles["/margin-analyzer"]} user={user}>
            <PricingToolsPage initialTab="margin" />
          </RequireRoles>
        }
      />
      <Route
        path="/staffing-requests"
        element={
          <RequireRoles allowedRoles={routeRoles["/staffing-requests"]} user={user}>
            {user?.role === "client" ? <StaffingRequestsPage /> : <Navigate to="/master-staffing-board" replace />}
          </RequireRoles>
        }
      />
      <Route
        path="/master-staffing-board"
        element={
          <RequireRoles allowedRoles={routeRoles["/master-staffing-board"]} user={user}>
            <MasterStaffingBoardPage />
          </RequireRoles>
        }
      />
      <Route
        path="/candidates"
        element={
          <RequireRoles allowedRoles={routeRoles["/candidates"]} user={user}>
            <CandidatesPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-review"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-review"]} user={user}>
            <ClientReviewPage />
          </RequireRoles>
        }
      />
      <Route
        path="/assignments"
        element={
          <RequireRoles allowedRoles={routeRoles["/assignments"]} user={user}>
            <AssignmentsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/clients"
        element={
          <RequireRoles allowedRoles={routeRoles["/clients"]} user={user}>
            <ClientsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/client-accounts"
        element={
          <RequireRoles allowedRoles={routeRoles["/client-accounts"]} user={user}>
            <ClientAccountsPage />
          </RequireRoles>
        }
      />
      <Route
        path="/team"
        element={
          <RequireRoles allowedRoles={routeRoles["/team"]} user={user}>
            <TeamDirectoryPage />
          </RequireRoles>
        }
      />
      <Route
        path="/reports"
        element={
          user?.role === "client" ? (
            <Navigate to="/portal" replace />
          ) : (
            <RequireRoles allowedRoles={routeRoles["/reports"]} user={user}>
              <ReportsPage />
            </RequireRoles>
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <InstallPrompt />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
